import {
    Button,
    Dialog,
    DialogBody,
    DialogContainer,
    DialogFooter,
    DialogHeader,
    DialogTitle,
    DialogTrigger,
    ErrorDisplay,
    Input,
    Label,
} from "@/shared/components";
import { normalizeError } from "@/shared/utils";
import { useId, useState } from "react";

const isAllowedExtension = (fileName: string, extension: string) => fileName.toLowerCase().endsWith(extension.toLowerCase());

type UploadFileDialogProps = {
    children: React.ReactNode; // the button
    title: string;
    label: string;
    handleFileUpload: (file: File) => Promise<void>;
    isPending: boolean;
    allowedExtensions?: string[];
};

function UploadFileDialog({ children, title, label, handleFileUpload, allowedExtensions, isPending = false }: UploadFileDialogProps) {
    const [open, setOpen] = useState(false);
    const [error, setError] = useState<Error | null>(null);

    const id = useId();

    const normalizedExtensions = allowedExtensions?.map((ext) => (ext.startsWith(".") ? ext : `.${ext}`));

    async function handleSubmit(e: React.FormEvent<HTMLFormElement>) {
        e.preventDefault();
        setError(null);
        const formData = new FormData(e.currentTarget);
        const file = formData.get("file") as File;

        if (!file.size) {
            setError(new Error("Selecteer een bestand"));
            return;
        }

        if (normalizedExtensions?.length && !normalizedExtensions.some((extension) => isAllowedExtension(file.name, extension))) {
            setError(new Error(`Ongeldig bestandstype. Toegestane types: ${normalizedExtensions.join(", ")}`));
            return;
        }

        try {
            await handleFileUpload(file);
            setOpen(false);
        } catch (error) {
            setError(normalizeError(error));
            // Dialog stays open so user can try again
        }
    }

    return (
        <Dialog
            open={open}
            onOpenChange={(open) => {
                setError(null);
                setOpen(open);
            }}
        >
            {children && <DialogTrigger asChild>{children}</DialogTrigger>}
            <DialogContainer>
                <form
                    onSubmit={(e) => {
                        void handleSubmit(e);
                    }}
                    className="grid gap-2"
                >
                    <DialogHeader>
                        <DialogTitle>{title}</DialogTitle>
                    </DialogHeader>
                    <DialogBody>
                        <Label htmlFor={id}>{label}</Label>
                        <Input
                            id={id}
                            name="file"
                            type="file"
                            className="md:text-s w-auto"
                            accept={normalizedExtensions?.length ? normalizedExtensions.join(",") : undefined}
                            onChange={() => setError(null)}
                        />
                        {error && <ErrorDisplay error={error} />}
                    </DialogBody>
                    <DialogFooter>
                        <Button type="button" variant="tertiary" onClick={() => setOpen(false)}>
                            Annuleren
                        </Button>
                        <Button type="submit" variant="primary" disabled={isPending}>
                            Uploaden
                        </Button>
                    </DialogFooter>
                </form>
            </DialogContainer>
        </Dialog>
    );
}

export { UploadFileDialog };
